import React, { useEffect, useState } from 'react';
import { CompanyForm } from '../components/company/CompanyForm';
import { Client, CoAuthor } from '../types';
import { customerApi } from '../services/api';
import { toast } from 'react-toastify';
import { HelpButton } from '../components/common/HelpButton';
import { useCustomer } from '../context/CustomerContext';

interface CompanyDetailsProps {
  onShowHelp?: () => void;
}

export const CompanyDetails: React.FC<CompanyDetailsProps> = ({ onShowHelp }) => {
  const { customerData, updateCustomerData } = useCustomer();
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizeCoAuthors = (coAuthors: Array<string | CoAuthor> = []): CoAuthor[] =>
    coAuthors
      .map((coAuthor) =>
        typeof coAuthor === 'string'
          ? { user_id: '', email: coAuthor }
          : { user_id: coAuthor.user_id || '', email: coAuthor.email }
      )
      .filter((coAuthor) => Boolean(coAuthor.email));

  useEffect(() => {
    // If customerData is provided, map it to the Client interface
    if (customerData) {
      const mappedClient: Client = {
        id: customerData.customer_id || '',
        name: customerData.customer_details?.name || '',
        industry: customerData.customer_details?.industry_type || '',
        profile: customerData.customer_details?.company_profile || '',
        relationship: customerData.customer_details?.relationship_summary || '',
        ERPAccountNumber: customerData.customer_details?.ERPAccountNumber || '',
        co_authors: normalizeCoAuthors(customerData.customer_details?.co_authors),
      };
      setClient(mappedClient);
      setIsLoading(false);
    }
  }, [customerData]);

  const handleSave = async (updatedClient: Client) => {
    try {
      // If we have customerData, we should update it with the new values
      if (customerData) {
        const customerId = customerData.customer_id;
        
        // Prepare the request body according to the specified format
        const requestBody = {
          sections: [
            {
              section_name: "customer_details",
              data: {
                name: updatedClient.name,
                relationship_summary: updatedClient.relationship,
                company_profile: updatedClient.profile,
                industry_type: updatedClient.industry,
                ERPAccountNumber: updatedClient.ERPAccountNumber || '',
                co_authors: updatedClient.co_authors || [],
              }
            }
          ]
        };
        
        try {
          // Use the customerApi.updateCustomerDetails function instead of direct fetch
          await customerApi.updateCustomerDetails(customerId, requestBody);
          
          // Update the local state
          setClient(updatedClient);
          
          // Update context with the new customer details
          if (customerData) {
            updateCustomerData({
              customer_details: {
                ...customerData.customer_details,
                name: updatedClient.name,
                relationship_summary: updatedClient.relationship,
                company_profile: updatedClient.profile,
                industry_type: updatedClient.industry,
                ERPAccountNumber: updatedClient.ERPAccountNumber || '',
                co_authors: updatedClient.co_authors || [],
              }
            });
          }
          
          // Show success notification
          toast.success('Company details updated successfully!');
        } catch (error: unknown) {
          const apiError = error as Error;
          console.error('API Error:', apiError);
          
          // Even if the API call fails, update the local state for demo purposes
          setClient(updatedClient);
          
          // Show error notification
          toast.error(`API Error: ${apiError.message}. Local state updated for demo purposes.`);
        }
      }
    } catch (error) {
      console.error('Error updating client:', error);
      // Show error notification
      toast.error('Failed to update company details.');
    }
  };

  return (
    <>
      <div className="px-4">
        <div className="mb-6 flex justify-between items-center">
          <div className="flex gap-3 items-center">
            <h1 className="text-2xl font-bold italic text-[#0F2A4F]">Company Details</h1>
            {onShowHelp && <HelpButton onClick={onShowHelp} />}
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center py-10 items-center h-full">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0F2A4F]"></div>
          </div>
        ) : (
          client && <CompanyForm client={client} onSave={handleSave} />
        )}
      </div>
    </>
  );
};
